import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";
import { isEqual } from "lodash";

import { clearAttempts, updateAttempts } from "../../../api";
import useUserAttempts, { Attempt } from "../../../hooks/useAttempts";
import useCompetition, {
  useCompetitionEntry,
  useCompetitionJudgesUUIDsToUsers,
  useCompetitionRouteAttemptsJudgePermissions,
} from "../../../hooks/useCompetition";
import usePrevious from "../../../hooks/usePrevious";
import { putQueue, useRouteQueueUserUUIDs } from "../../../hooks/useRouteQueue";
import { useJudgeAuthWall } from "../../../modules/onboarding/components/hooks/useAuthWall";
import useCurrentUser from "../../../hooks/useCurrentUser";
import getCompetitionRouteDisplay from "../../../utils/getCompetitionRouteDisplay";

const removeMultipleTops = (attempts: Attempt[]) => {
  const i = attempts.findIndex((x) => x.success);
  if (i !== -1) {
    return [...attempts.slice(0, i + 1)];
  }
  return attempts;
};

export default function useAttemptsScreen({
  onSubmitSuccess,
  onSubmitError,
}: {
  onSubmitSuccess?: () => void;
  onSubmitError?: (error: unknown) => void;
} = {}) {
  useJudgeAuthWall();

  const judgeUUID = useCurrentUser().profile?.uuid;

  const { userUUID, competitionUUID, routeUUID } = useParams();

  const { competition } = useCompetition(competitionUUID);
  const { entry } = useCompetitionEntry({ userUUID, competitionUUID });

  const { queue } = useRouteQueueUserUUIDs({ routeUUID, competitionUUID });
  const queuePosition = userUUID && queue?.indexOf(userUUID);

  const venueUUID = competition?.venues?.[0]?.uuid;

  const judgesByUUID = useCompetitionJudgesUUIDsToUsers(competition);

  const {
    route,
    attempts: existingAttempts,
    isValidating,
    error,
  } = useUserAttempts({
    routeUUID,
    venueUUID,
    userUUID,
  });

  const routeName =
    route &&
    entry &&
    getCompetitionRouteDisplay({
      route,
      categoryUUID: entry?.category?.uuid,
      routeGroups: route?.competitionRouteGroups,
    })?.name;

  const [attempts, setAttempts] = useState(existingAttempts);

  const judgePermissions = useCompetitionRouteAttemptsJudgePermissions({
    competition,
    routeUUID,
    judgeUUID,
    attempts: existingAttempts,
  });

  const setAttemptSingle = (attempt: Attempt) => setAttempts([attempt]);

  const previousAttempts = usePrevious(existingAttempts);

  useEffect(() => {
    // Set attemptsInitial on load, if attemptsInitial is unset.
    if (existingAttempts && !previousAttempts) {
      setAttempts(existingAttempts);
    }
  }, [previousAttempts, existingAttempts]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<undefined | unknown>(
    undefined,
  );

  // `isDirty` tracks whether the draft differs from the persisted attempts.
  // `isTouched` tracks whether the judge has interacted with the attempts UI;
  // clearing it also tells the boulder input to reset which rows start open.
  const isDirty = useMemo(() => {
    return (
      existingAttempts &&
      attempts &&
      (attempts.length !== existingAttempts.length ||
        attempts.some(
          (x, i) =>
            x.success !== existingAttempts[i]?.success ||
            x.segmentsCount !== existingAttempts[i]?.segmentsCount ||
            x.usedLastHold !== existingAttempts[i]?.usedLastHold ||
            x.notes !== existingAttempts[i]?.notes ||
            x.duration !== existingAttempts[i]?.duration,
        ))
    );
  }, [attempts, existingAttempts]);

  useEffect(() => {
    isDirty && setIsTouched(isDirty);
  }, [isDirty]);

  const [isTouched, setIsTouched] = useState(false);

  const resetAttempts = async () => {
    if (routeUUID && userUUID) {
      setIsSubmitting(true);
      try {
        await clearAttempts({ routeUUID, userUUID });
        setSubmitError(null);
        onSubmitSuccess?.();
      } catch (err) {
        setSubmitError(err);
        onSubmitError?.(err);
      } finally {
        setIsSubmitting(false);
      }
      mutate(`/routes/${routeUUID}/attempts?user=${userUUID}`);
      mutate(`/routes/${routeUUID}/attempts?user=ALL`);
      mutate(`/competitions/${competitionUUID}/entries/${userUUID}`);
    }
  };

  const saveAttempts = async (updatedAttempts: Attempt[]) => {
    if (routeUUID && userUUID && existingAttempts) {
      setIsSubmitting(true);
      try {
        if (
          existingAttempts.length < updatedAttempts.length &&
          isEqual(
            existingAttempts.map((x) => ({
              uuid: x.uuid,
              success: x.success,
              segmentsCount: x.segmentsCount,
            })),
            updatedAttempts.slice(0, existingAttempts.length).map((x) => ({
              uuid: x.uuid,
              success: x.success,
              segmentsCount: x.segmentsCount,
            })),
          )
        ) {
          // If the persisted prefix is unchanged, we can append only the new
          // attempts instead of clearing and rewriting the whole list.
          await updateAttempts({
            routeUUID,
            userUUID,
            attempts: updatedAttempts
              .slice(existingAttempts.length)
              .map((x) => ({
                success: x.success,
                segmentsCount: x.segmentsCount,
                timestamp: x.timestamp,
                uuid: undefined,
                usedLastHold: x.usedLastHold,
                duration: x.duration,
                notes: x.notes,
              })),
          });
        } else {
          await clearAttempts({ routeUUID, userUUID });
          await updateAttempts({
            routeUUID,
            userUUID,
            attempts: updatedAttempts.map((x) => ({
              success: x.success,
              segmentsCount: x.segmentsCount,
              timestamp: x.timestamp,
              uuid: undefined,
              usedLastHold: x.usedLastHold,
              duration: x.duration,
              notes: x.notes,
            })),
          });
        }
        setSubmitError(null);
        onSubmitSuccess?.();
      } catch (err) {
        setSubmitError(err);
        onSubmitError?.(err);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
      mutate(`/routes/${routeUUID}/attempts?user=${userUUID}`);
      mutate(`/routes/${routeUUID}/attempts?user=ALL`);
      mutate(`/competitions/${competitionUUID}/entries/${userUUID}`);
    }
  };

  const navigate = useNavigate();

  const onBoulderSave = () => {
    const atts = removeMultipleTops(attempts || []);
    setAttempts(atts);
    saveAttempts(atts).then(() => {
      setIsTouched(false);
      if (!queue || !routeUUID || !userUUID || !competitionUUID) {
        return;
      }
      if (atts.length > (existingAttempts?.length || 0)) {
        navigate(-1);
        putQueue({
          routeUUID,
          competitionUUID,
          queue: queue.filter((x) => x !== userUUID),
        });
      }
    });
  };

  const onSportSave = () => {
    saveAttempts(attempts || []).then(() => {
      setIsTouched(false);
      if (!queue || !routeUUID || !userUUID || !competitionUUID) {
        return;
      }
      putQueue({
        routeUUID,
        competitionUUID,
        queue: queue.filter((x) => x !== userUUID),
      });
      navigate(-1);
    });
  };

  const onSave = route?.climbType === "BOULDER" ? onBoulderSave : onSportSave;

  const onClear = () => {
    resetAttempts();
    setAttempts([]);
  };

  const onSkip = () => {
    if (!queue || !routeUUID || !userUUID || !competitionUUID) {
      return;
    }
    putQueue({
      routeUUID,
      competitionUUID,
      queue: queue.filter((x) => x !== userUUID),
    });
    navigate(-1);
  };

  return {
    route,
    routeName,
    competition,
    entry,
    attempts,
    isValidating,
    error,
    isSubmitting,
    submitError,
    clearAttempts: resetAttempts,
    saveAttempts,
    setAttempts,
    setAttemptSingle,
    onSave,
    onClear,
    isTouched,
    setIsTouched,
    isDirty,
    existingAttempts,
    queuePosition,
    onSkip,
    judgePermissions,
    judgesByUUID,
    judgeUUID,
  };
}
